import { useEffect, useMemo, useRef, useState } from 'react';
import { CONFETTI_COLORS, CREAM, INK, LINE, MUTED, ORANGE, PAPER, SHADOW_CARD, SUN, SURFACE_SOFT, CORAL_DEEP, MOTION, CORAL } from './theme.ts';
import { reducedMotion } from '../engine/haptics.ts';
import { asset } from '../engine/assets.ts';
import { Icon } from './Icon.tsx';

/** 18 particles, 5 colours, staggered 50 ms — suppressed under reduced motion. */
export function Confetti({ run = true }: { run?: boolean }) {
  const particles = useMemo(
    () =>
      Array.from({ length: 18 }, (_, i) => {
        const a = (i / 18) * Math.PI * 2;
        const r = 110 + (i % 3) * 40;
        return { i, dx: Math.cos(a) * r, dy: Math.sin(a) * r, color: CONFETTI_COLORS[i % 5], round: i % 2 === 1 };
      }),
    [],
  );
  if (!run || reducedMotion()) return null;
  return (
    <div style={{ position: 'absolute', left: '50%', top: -40, pointerEvents: 'none' }} aria-hidden="true">
      {particles.map((p) => (
        <span
          key={p.i}
          style={
            {
              position: 'absolute',
              width: 10,
              height: p.round ? 10 : 6,
              borderRadius: p.round ? '50%' : 2,
              background: p.color,
              '--dx': `${p.dx}px`,
              '--dy': `${p.dy}px`,
              animation: `fk-confetti ${MOTION.confetti}ms ease-out ${(p.i % 4) * 50}ms forwards`,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}

export function Toast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div
      role="status"
      style={{
        position: 'absolute',
        left: 20,
        right: 20,
        bottom: 100,
        padding: '12px 16px',
        borderRadius: 16,
        background: INK,
        color: '#fff',
        fontWeight: 700,
        fontSize: 14,
        textAlign: 'center',
        animation: 'fk-in .2s ease-out',
      }}
    >
      {message}
    </div>
  );
}

export function Ring({ size = 96, inner = 80, background = PAPER }: { size?: number; inner?: number; background?: string }) {
  return (
    <span
      style={{
        position: 'relative',
        width: size,
        height: size,
        borderRadius: '50%',
        background: `conic-gradient(${CORAL}, ${ORANGE}, ${LINE} 75%, ${LINE})`,
        animation: 'fk-spin 1.4s linear infinite',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      aria-hidden="true"
    >
      <span style={{ width: inner, height: inner, borderRadius: '50%', background }} />
    </span>
  );
}

/** cream stat tile; `highlight` paints it with the sun gradient. */
export function StatTile({ value, caption, highlight = false }: { value: string; caption: string; highlight?: boolean }) {
  return (
    <div
      style={{
        padding: 'var(--fk-card-pad)',
        borderRadius: 20,
        background: highlight ? SUN : CREAM,
        border: highlight ? '2px solid transparent' : `2px solid ${LINE}`,
      }}
    >
      <div style={{ fontSize: 'var(--fk-stat)', fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1 }}>{value}</div>
      <div className="fk-copy" style={{ fontSize: 'clamp(11px, 3.2vw, 13px)', fontWeight: 600, color: highlight ? INK : MUTED, marginTop: 3 }}>{caption}</div>
    </div>
  );
}

export function FactTile({ text }: { text: string }) {
  return (
    <div style={{ width: '100%', padding: 'var(--fk-card-pad)', borderRadius: 20, background: CREAM, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <Icon name="lightbulb" size={22} color={CORAL_DEEP} style={{ flex: 'none' }} />
      <div style={{ fontSize: 14, fontWeight: 600 }}>{text}</div>
    </div>
  );
}

/**
 * 16:9 frame — poster first, `src` attached on mount, removed on unmount
 * (ASSETS.md). Falls back to the still when no clip exists for the slot.
 */
export function VideoFrame({
  slot,
  aspect = '16/9',
  radius = 24,
  overlay,
  muteBadge = true,
  play = true,
  maxHeight,
  muteLabel = 'muted · loop',
}: {
  slot: string;
  aspect?: string;
  radius?: number;
  overlay?: React.ReactNode;
  muteBadge?: boolean;
  play?: boolean;
  maxHeight?: number;
  muteLabel?: string;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const ref = useRef<HTMLVideoElement | null>(null);
  const poster = asset(slot);
  const clip = poster.replace(/\.(png|webp)(\?.*)?$/, '.mp4');

  useEffect(() => {
    let alive = true;
    // Only attach a clip that is really there. A 200 is not enough: the SPA
    // fallback answers index.html for any unknown path, and pointing <video> at
    // that renders a black box where the poster should be.
    fetch(clip, { method: 'HEAD' })
      .then((r) => {
        const type = r.headers.get('content-type') ?? '';
        if (alive && r.ok && type.startsWith('video/')) setSrc(clip);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      if (ref.current) ref.current.removeAttribute('src');
    };
  }, [clip]);

  return (
    <div
      style={{
        flex: 'none',
        position: 'relative',
        width: '100%',
        aspectRatio: aspect,
        maxHeight,
        borderRadius: radius,
        overflow: 'hidden',
        background: SURFACE_SOFT,
        boxShadow: SHADOW_CARD,
      }}
    >
      {src ? (
        <video
          ref={ref}
          src={src}
          poster={poster}
          muted
          playsInline
          autoPlay
          loop
          preload="metadata"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <img src={poster} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      {overlay}
      {play ? (
        <span
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: 'rgba(255,255,255,.92)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 6px 20px rgba(31,27,45,.12)',
          }}
        >
          <Icon name="play" size={22} />
        </span>
      ) : null}
      {muteBadge ? (
        <span
          style={{
            position: 'absolute',
            right: 10,
            bottom: 10,
            padding: '4px 8px',
            borderRadius: 999,
            background: 'rgba(31,27,45,.7)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 800,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <Icon name="speaker-slash" size={13} />
          {muteLabel}
        </span>
      ) : null}
    </div>
  );
}
